"use client";

import { SafeTryItImg } from "@/components/customer/image-utils";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

type MobileEmptyStateAction = {
  label: string;
  onClick?: () => void;
  href?: string;
};

function resolveMobileEmptyStateIllustration({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  const text = `${title} ${subtitle ?? ""}`.toLowerCase();

  if (
    /\b(order|orders|delivery|deliveries|cart|checkout|line items|meal history|previous meals|shared order)\b/.test(text)
  ) {
    return "/mobile-illustrations/orders-empty-flat.png";
  }

  if (
    /\b(family|member|members|invite|invites|friends|group|shared)\b/.test(text)
  ) {
    return "/app-downloads/creator-preview.png";
  }

  if (
    /\b(creator|creators|recommendation|recommendations|discover|search|content|recipe|recipes|story|stories|video|videos|trending|notification|notifications|reply|replies|comment|comments)\b/.test(text)
  ) {
    return "/section-doodles/food-content-doodle.png";
  }

  return "/section-doodles/food-content-doodle.png";
}

export function MobileEmptyState({
  icon: Icon,
  iconElement,
  illustrationSrc,
  illustrationAlt = "",
  illustrationClassName = "",
  title,
  subtitle,
  action,
  secondaryAction,
  children,
  className = "",
  align = "center",
  contentClassName = "",
  actionsClassName = "",
}: {
  icon?: LucideIcon;
  iconElement?: ReactNode;
  illustrationSrc?: string;
  illustrationAlt?: string;
  illustrationClassName?: string;
  title: string;
  subtitle?: string;
  action?: MobileEmptyStateAction;
  secondaryAction?: MobileEmptyStateAction;
  children?: ReactNode;
  className?: string;
  align?: "center" | "start";
  contentClassName?: string;
  actionsClassName?: string;
}) {
  const isStartAligned = align === "start";
  const resolvedIllustrationSrc =
    illustrationSrc ?? resolveMobileEmptyStateIllustration({ title, subtitle });
  const resolvedIllustrationClassName =
    illustrationClassName || (resolvedIllustrationSrc ? "h-[160px] w-[160px]" : "");

  return (
    <div
      className={`flex min-h-[240px] w-full px-6 py-9 ${
        isStartAligned ? "items-start justify-start text-left" : "items-center justify-center text-center"
      } ${className}`}
    >
      <div
        className={`flex w-full flex-col ${
          isStartAligned ? "max-w-[420px] items-start" : "max-w-[360px] items-center"
        } ${contentClassName}`}
      >
        <div className="mb-[18px] inline-flex items-center justify-center text-slate-400">
          {resolvedIllustrationSrc ? (
            <SafeTryItImg
              alt={illustrationAlt}
              src={resolvedIllustrationSrc}
              containerClassName={`relative block max-w-full overflow-hidden ${resolvedIllustrationClassName}`}
              className="h-full w-full object-contain"
            />
          ) : iconElement ? (
            <div className="flex items-center justify-center">{iconElement}</div>
          ) : Icon ? (
            <div className="flex h-14 w-14 items-center justify-center rounded-[18px] border border-slate-200/80 bg-[linear-gradient(135deg,rgba(156,163,175,0.08),rgba(156,163,175,0.03))] shadow-[0_8px_14px_rgba(17,24,39,0.05)]">
              <Icon size={28} />
            </div>
          ) : null}
        </div>
        <h3 className="max-w-[300px] text-xl font-bold leading-[26px] text-slate-950">{title}</h3>
        {subtitle ? (
          <p className="mt-2 max-w-[310px] text-sm leading-[21px] text-slate-500">{subtitle}</p>
        ) : null}
        {(action || secondaryAction) ? (
          <div
            className={`mt-6 grid w-full gap-3 ${
              isStartAligned ? "max-w-[320px]" : "max-w-[280px]"
            } ${actionsClassName}`}
          >
            {action ? <MobileEmptyStateActionButton action={action} primary /> : null}
            {secondaryAction ? <MobileEmptyStateActionButton action={secondaryAction} /> : null}
          </div>
        ) : null}
        {children ? <div className={`mt-1 ${isStartAligned ? "w-full" : ""}`}>{children}</div> : null}
      </div>
    </div>
  );
}

export function MobileNotLoggedInNotice({
  title = "Looks Good, Right?",
  subtitle = "Log in to make it yours. personalize it",
  buttonLabel = "Sign in",
  onSignInPress,
  className = "",
  titleClassName = "",
  subtitleClassName = "",
  buttonClassName = "",
}: {
  title?: string;
  subtitle?: string;
  buttonLabel?: string;
  onSignInPress: () => void;
  className?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  buttonClassName?: string;
}) {
  return (
    <div className={`w-full px-4 py-5 ${className}`}>
      <h3 className={`text-[20px] font-bold leading-8 text-[#094327] ${titleClassName}`}>{title}</h3>
      <p className={`mt-2 text-[16px] font-normal leading-[26px] text-black ${subtitleClassName}`}>{subtitle}</p>
      <button
        type="button"
        onClick={onSignInPress}
        className={`mt-5 flex h-[54px] w-full items-center justify-center rounded-[11px] bg-[#F0F3FF] px-5 text-[18px] font-bold leading-[27px] text-[#FF3B30] transition hover:bg-[#e8edff] ${buttonClassName}`}
      >
        {buttonLabel}
      </button>
    </div>
  );
}

function MobileEmptyStateActionButton({
  action,
  primary = false,
}: {
  action: MobileEmptyStateAction;
  primary?: boolean;
}) {
  const className = primary
    ? "inline-flex min-h-12 items-center justify-center rounded-[14px] bg-[#ff3b30] px-5 py-3.5 text-[15px] font-bold text-white shadow-[0_8px_14px_rgba(255,59,48,0.18)] transition hover:bg-[#f13228]"
    : "inline-flex min-h-12 items-center justify-center rounded-[14px] border border-slate-200 bg-white/75 px-5 py-3.5 text-[15px] font-bold text-slate-950 shadow-sm transition hover:bg-white";

  if (action.href) {
    return (
      <a href={action.href} className={className}>
        {action.label}
      </a>
    );
  }

  return (
    <button type="button" onClick={action.onClick} className={className}>
      {action.label}
    </button>
  );
}

export function MobileLoadingRows({
  itemCount = 3,
  className = "",
}: {
  itemCount?: number;
  className?: string;
}) {
  return (
    <div className={`space-y-2 px-1 ${className}`}>
      {Array.from({ length: itemCount }).map((_, index) => (
        <div key={index} className="flex items-center rounded-xl bg-white/30 px-3 py-4">
          <div className="mr-4 h-[60px] w-[60px] shrink-0 animate-pulse rounded-xl bg-slate-300/30" style={{ animationDelay: `${index * 90}ms` }} />
          <div className="min-w-0 flex-1">
            <div className="mb-2 h-4 animate-pulse rounded bg-slate-300/30" style={{ animationDelay: `${index * 90 + 80}ms` }} />
            <div className="mb-2 h-4 w-3/5 animate-pulse rounded bg-slate-300/30" style={{ animationDelay: `${index * 90 + 140}ms` }} />
            <div className="h-3.5 w-1/3 animate-pulse rounded bg-slate-300/30" style={{ animationDelay: `${index * 90 + 200}ms` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function MobileNotice({
  tone = "info",
  title,
  detail,
  onDismiss,
  className = "",
}: {
  tone?: "info" | "working" | "error";
  title: string;
  detail?: string;
  onDismiss?: () => void;
  className?: string;
}) {
  const toneClasses =
    tone === "error"
      ? "border-[#FF3B30]/20 bg-[#FFF4F2] text-[#991B1B]"
      : tone === "working"
        ? "border-[#094327]/20 bg-[#E8F7EC] text-[#094327]"
        : "border-slate-200 bg-white/80 text-slate-700";

  return (
    <div className={`rounded-[18px] border px-4 py-3 shadow-sm ${toneClasses} ${className}`}>
      <div className="flex items-start gap-3">
        <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${tone === "error" ? "bg-[#FF3B30]" : "bg-[#094327]"}`} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-black leading-5">{title}</p>
          {detail ? <p className="mt-1 text-sm font-semibold leading-5 opacity-80">{detail}</p> : null}
        </div>
        {onDismiss ? (
          <button type="button" onClick={onDismiss} className="text-xs font-black opacity-70 transition hover:opacity-100">
            Dismiss
          </button>
        ) : null}
      </div>
    </div>
  );
}
